import pickBy from 'lodash/pickBy';

import exporter from '../exporter';
import { createAction } from '../actions';
import featuresConfig from '../../config/features.json';
import { FeatureKey } from '../../types/commons';
import { PayloadAction } from '../../types/state';

// ~~~~~~~~~~ TYPES ~~~~~~~~~~
// Only features that are a/b testable via query params are allowed in this persisted duck
const abTestableFeatures = (Object.keys(featuresConfig) as FeatureKey[]).filter(
  (name) => !!featuresConfig[name].isAbTestable,
);

// AbFeatures is a union type based on the content of the features in config
export type AbFeatures = Partial<Record<FeatureKey, boolean>>;

export type AbFeaturesState = Readonly<{
  all: AbFeatures;
}>;

type EnableFeatureAction = PayloadAction<FeatureKey>;
type DisableFeatureAction = PayloadAction<FeatureKey>;

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~
export const ENABLE_FEATURE = 'FEATURES_ENABLE';
export const DISABLE_FEATURE = 'FEATURES_DISABLE';
export const REMOVE_OBSOLETE_AB_FEATURES = 'REMOVE_OBSOLETE_AB_FEATURES';

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const enableAbFeature = (feature: FeatureKey) => createAction(ENABLE_FEATURE, feature);
export const disableAbFeature = (feature: FeatureKey) => createAction(DISABLE_FEATURE, feature);
export const removeObsoleteAbFeatures = () => createAction(REMOVE_OBSOLETE_AB_FEATURES, {});

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~

export const initialState: AbFeaturesState = {
  all: {},
};

const removeObsolete = (values: AbFeatures) =>
  pickBy(values, (_value, featureKey) => abTestableFeatures.includes(featureKey as FeatureKey));

const onRemoveObsolete = (state: AbFeaturesState) => ({
  all: removeObsolete({ ...state.all }),
});

const onEnableFeature = (state: AbFeaturesState, action: EnableFeatureAction) => ({
  ...state,
  all: removeObsolete({ ...state.all, [action.payload]: true }),
});

const onDisableFeature = (state: AbFeaturesState, action: DisableFeatureAction) => ({
  ...state,
  all: removeObsolete({ ...state.all, [action.payload]: false }),
});

const handlers = {
  [ENABLE_FEATURE]: onEnableFeature,
  [DISABLE_FEATURE]: onDisableFeature,
  [REMOVE_OBSOLETE_AB_FEATURES]: onRemoveObsolete,
};

export const reducer = exporter<AbFeaturesState>(initialState, handlers);
